// Role-aware home-screen task buttons (BUILD_SPEC.md §11: "big task buttons — Receive
// a load / Record a shift / Silo reading / Dispatch a load / Raise invoices / Issue
// stores"). Role gates follow §6's route-group matrix (OPERATIONS: tickets, receipts,
// runs, counts, issues, transfers, silo readings; SALES_ACCOUNTS: dispatch approval,
// invoices). Each target routes to its screen-group's guided flow (§8).
import type { Icon } from '@tabler/icons-react';
import { IconGauge, IconPackageExport, IconReceipt2, IconStack2, IconTruck, IconTruckDelivery } from '@tabler/icons-react';
import type { Role } from '@zebra/core';

export interface HomeTask {
  label: string;
  to: string;
  icon: Icon;
  roles: Role[];
  /** One-line helper copy shown under the label on the Home task grid
   * (design-uplift goal, leverage point 3) — purely presentational, never read
   * for routing/role logic. Optional so Shell's "Quick actions" menu (which
   * reuses this same list) isn't required to render it. */
  description?: string;
  /** Hidden when the active tenant's silo module is off (lib/tenantFeatures.ts
   * — Stuart ruling 2026-08-26, default OFF). Only 'Silo reading' sets this
   * today; every other task is unconditional on top of its role gate. */
  requiresSilos?: true;
}

export const HOME_TASKS: HomeTask[] = [
  {
    label: 'Receive a load',
    to: '/purchasing-intake',
    icon: IconTruckDelivery,
    roles: ['OPERATIONS', 'ADMIN'],
    description: 'Weigh in a delivery and post it to stock',
  },
  {
    label: 'Record a shift',
    to: '/production-packconv',
    icon: IconStack2,
    roles: ['OPERATIONS', 'ADMIN'],
    description: 'Log a drying, milling or packing run',
  },
  // Points at the silo status overview directly, not the section root — the
  // section root now redirects to stock counts (counts-first ruling,
  // 2026-08-24), which would otherwise silently retarget this button.
  {
    label: 'Silo reading',
    to: '/silo-counts/status',
    icon: IconGauge,
    roles: ['OPERATIONS', 'ADMIN'],
    description: 'Dip a silo and reconcile against the ledger',
    requiresSilos: true,
  },
  {
    label: 'Dispatch a load',
    to: '/sales-invoicing',
    icon: IconTruck,
    roles: ['OPERATIONS', 'SALES_ACCOUNTS', 'ADMIN'],
    description: 'Weigh out an order and release it to a customer',
  },
  {
    label: 'Raise invoices',
    to: '/sales-invoicing/raise',
    icon: IconReceipt2,
    roles: ['SALES_ACCOUNTS', 'ADMIN'],
    description: 'Turn uninvoiced dispatches into customer invoices',
  },
  {
    label: 'Issue stores',
    to: '/stores-transfers',
    icon: IconPackageExport,
    roles: ['OPERATIONS', 'ADMIN'],
    description: 'Issue consumables to a cost centre or transfer stock',
  },
];
